import React, {useEffect, useState, useCallback, useRef} from "react";
import {hasUserCompleted, hasUserCompletedLesson} from "../util/UserProgressHelper";
import {BCIcon, ICONS} from "./generic/BCIcons";
import {BCHeader4, BCHeader6} from "./generic/BCHeaders";
import {FlexCol, FlexRow} from "./generic/Containers";
import {Course, Lesson} from "../models/Course";
import VideoPlayer from "./VideoPlayer";
import "../css/CoursePage.css"
import '../css/LearningPathContent.css'
import 'react-circular-progressbar/dist/styles.css';
import {useGlobalState} from "../GlobalState";
import {markLessonAsCompleted} from "../util/ProgressTrackingService";
import {trackEvent, TRACKING_EVENTS} from "../util/TrackingHelper";


export const CourseLessonsDashboard: React.FC<{ course: Course }> = ({course}) => {
    const { isAuthenticated, userProgressData, user, setUserProgressData } = useGlobalState();
    const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(course.sections[0].lessons[0]);
    const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
    const [isLessonCompleting, setIsLessonCompleting] = useState(false);

    // Use refs for values that change but shouldn't trigger callback recreation
    const userRef = useRef(user);
    const isAuthenticatedRef = useRef(isAuthenticated);

    // Update refs when values change
    useEffect(() => {
        userRef.current = user;
        isAuthenticatedRef.current = isAuthenticated;
    });

    // Stable completion handler that doesn't depend on changing state values
    const handleLessonCompletion = useCallback(async (lesson: Lesson) => {
        if (!isAuthenticatedRef.current || !userRef.current?.uid || !course.trackingId) return;
        
        // Use navUrl as unique identifier instead of lesson.id
        const uniqueLessonId = lesson.navUrl;
        
        setIsLessonCompleting(true);
        try {
            await markLessonAsCompleted(userRef.current.uid, course.trackingId, uniqueLessonId);
            
            // Track lesson completion event
            trackEvent(TRACKING_EVENTS.LESSON_COMPLETED, {
                course_id: course.trackingId,
                course_name: course.name,
                lesson_id: uniqueLessonId,
                lesson_name: lesson.fileName,
                user_id: userRef.current.uid
            });
            
            // Update state using functional update pattern to avoid dependencies
            setUserProgressData((currentProgressData: any) => {
                if (!currentProgressData) return currentProgressData;
                
                // Check if already completed to avoid duplicate updates
                const isCompleted = hasUserCompletedLesson(currentProgressData, course.trackingId, uniqueLessonId);
                if (isCompleted) {
                    return currentProgressData;
                }
                
                const updatedProgressData = { ...currentProgressData };
                
                // Ensure courses array exists
                if (!updatedProgressData.courses) {
                    updatedProgressData.courses = [];
                }
                
                // Find or create the course entry
                let courseEntry = updatedProgressData.courses.find((c: any) => c.id === course.trackingId);
                if (!courseEntry) {
                    courseEntry = {
                        id: course.trackingId,
                        hasCompleted: false,
                        displayInRoadMap: true,
                        lessons: []
                    };
                    updatedProgressData.courses.push(courseEntry);
                }
                
                // Find or create the lesson entry using unique navUrl
                let lessonEntry = courseEntry.lessons.find((l: any) => l.id === uniqueLessonId);
                if (!lessonEntry) {
                    lessonEntry = {
                        id: uniqueLessonId,
                        hasCompleted: false,
                        displayInRoadMap: true
                    };
                    courseEntry.lessons.push(lessonEntry);
                }
                
                // Mark lesson as completed
                lessonEntry.hasCompleted = true;
                
                // Check if all lessons are completed to mark entire course as complete
                const allLessons = course.sections.flatMap(section => section.lessons);
                const allLessonsComplete = allLessons.every(l => {
                    const existingLesson = courseEntry.lessons.find((existing: any) => existing.id === l.navUrl);
                    return existingLesson?.hasCompleted || false;
                });
                
                if (allLessonsComplete) {
                    courseEntry.hasCompleted = true;
                }
                
                return updatedProgressData;
            });
        } catch (error) {
            console.error('Error saving lesson completion:', error);
        } finally {
            setIsLessonCompleting(false);
        }
    }, [course.trackingId, setUserProgressData]); // Minimal stable dependencies

    return (
        <FlexCol vMargin={1} gap={1}>
            <FlexRow className={"dashboard-course-content-container"}>
                <FlexCol className={"dashboard-course-lesson-content"}>
                    {course.sections.map((section, index) => (
                        <FlexCol className={'course-section-container'} key={index}>
                            <FlexRow className={'course-section'} justify={"space-between"} align={"center"} padding={1}  onClick={() => {
                                setSelectedSectionIndex(index)
                                setSelectedLesson(course.sections[index].lessons[0]);
                            }}>
                                <h5 className={'course-section-header'}>{section.title}</h5>
                                {selectedSectionIndex === index && <BCIcon icon={ICONS.CHEVRON_DOWN} />}
                                {selectedSectionIndex !== index && <BCIcon icon={ICONS.CHEVRON_LEFT} />}
                            </FlexRow>
                            {selectedSectionIndex === index && section.lessons.map((lesson, index2) => {
                                // Use navUrl as unique identifier instead of lesson.id
                                const uniqueLessonId = lesson.navUrl;
                                const isCompleted = isAuthenticated && course.trackingId 
                                    ? hasUserCompletedLesson(userProgressData, course.trackingId, uniqueLessonId)
                                    : false;
                                
                                return (
                                    <FlexRow 
                                        key={index2} 
                                        className={`lesson-container ${lesson.id === selectedLesson?.id ? 'selected' : ''} ${isCompleted ? 'completed' : ''}`} 
                                        onClick={() => {
                                            setSelectedLesson(course.sections[index].lessons[index2]);
                                        }}
                                        style={{
                                            backgroundColor: isCompleted ? 'var(--success-background, #f8f9fa)' : undefined
                                        }}
                                    >
                                        <BCIcon 
                                            icon={isCompleted ? ICONS.CHECKMARK_CIRCLE : ICONS.SOLID_CIRCLE} 
                                            className={isCompleted ? 'lesson-icon-completed' : 'lesson-icon-not-started'}
                                            style={{
                                                color: isCompleted ? 'var(--success-color, #28a745)' : undefined
                                            }}
                                        />
                                        <BCHeader6 
                                            className={"lesson-text-header"} 
                                            text={lesson.title}
                                            style={{
                                                color: isCompleted ? 'var(--success-color, #28a745)' : undefined,
                                                fontWeight: isCompleted ? 'bold' : undefined
                                            }}
                                        />
                                        {isCompleted && (
                                            <span style={{
                                                fontSize: '0.75em',
                                                color: 'var(--success-color, #28a745)',
                                                fontWeight: 'bold',
                                                marginLeft: 'auto'
                                            }}>
                                                ✓
                                            </span>
                                        )}
                                    </FlexRow>
                                );
                            })}
                        </FlexCol>
                    ))}
                </FlexCol>
                {selectedLesson && (
                    <VideoPlayer
                        key={selectedLesson.fileName}
                        isPremium={true}
                        fileUrl={`courses/${selectedLesson.fileName}`}
                        isAuthenticated={isAuthenticated}
                        onVideoComplete={() => handleLessonCompletion(selectedLesson)}
                        isCompletingLesson={isLessonCompleting}
                    />
                )}
            </FlexRow>
        </FlexCol>
    )
};

export const GridItemIconInfo: React.FC<any> = ({icon: Icon, text}) => {
    return (
        <div className={"learning-grid-item-icon-container"}>
            <Icon/>
            <h5>{text}</h5>
        </div>
    )
}

export const RoadMapIcon: React.FC<{ type: string }> = ({type}) => {
    return (
        <div className={'roadmap-item-icon-container onboarding'}>
            <div className={`roadmap-item-icon ${type}`}>
                {type === "courses" && <BCIcon icon={ICONS.CHALKBOARD_TEACHER} />}
                {type === "interviews" && <BCIcon icon={ICONS.VIDEO} />}
                {type === "resources" && <BCIcon icon={ICONS.DOCUMENT} />}
                {type === "tools" && <BCIcon icon={ICONS.SPARKLE} />}
                {type === "actions" && <BCIcon icon={ICONS.PEN} />}
            </div>
            <div className={`roadmap-icon-name ${type}`}>{getTypeDisplayName(type)}</div>
        </div>
    )
}

export const getTypeDisplayName = (type: string) => {
    switch (type) {
        case "courses":
            return "Course"
        case "interviews":
            return "Interview"
        case "resources":
            return "Resource"
        case "tools":
            return "AI Tool"
        case "actions":
            return "Action Item"
    }
    return ""
}